import { useCallback, useEffect, useMemo, useRef } from 'react';
import PrintIcon from '@mui/icons-material/Print';
import { AxiosRequestConfig } from 'axios';
import CustomButton from 'src/core/ui/CustomButton';
import useRequest from 'src/core/api/useRequest';
import { getPrintInvoiceById } from 'src/core/api/apiRequests';

const PrintInvoiceButton = ({ invoiceId, title = 'Print', disabled, iconButton = false }: Props) => {
  const [apiResult, setRequest, , loading] = useRequest<Blob>();
  const userInitiatedRef = useRef(false);
  const printReq: AxiosRequestConfig | null = useMemo(() => {
    if (!invoiceId) return null;
    const req = getPrintInvoiceById(invoiceId);
    return { ...req, responseType: 'blob', headers: { ...(req.headers ?? {}), Accept: 'application/pdf', }, } as AxiosRequestConfig;
  }, [invoiceId]);
  const handleClick = useCallback(() => {
    if (!printReq) return;
    userInitiatedRef.current = true;
    setRequest(printReq);
  }, [printReq, setRequest]);
  const openAndDownloadPdf = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    try {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w && typeof w.focus === 'function') w.focus();
    } catch {
      // ignore
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }, []);
  useEffect(() => {
    if (!apiResult || !userInitiatedRef.current) return;
    userInitiatedRef.current = false;
    const blob = apiResult instanceof Blob ? apiResult : new Blob([apiResult as any], { type: 'application/pdf' });
    const filename = `invoice_${invoiceId}.pdf`;
    openAndDownloadPdf(blob, filename);
  }, [apiResult, invoiceId, openAndDownloadPdf]);

  return (
    <CustomButton title={loading ? 'Preparing…' : title} icon={<PrintIcon />} iconButton={iconButton} disabled={loading || !invoiceId || !!disabled} onClick={handleClick} />
  );
};

export default PrintInvoiceButton;

type Props = {
  invoiceId?: number | string | null;
  title?: string;                  // default: "Print"
  iconOnly?: boolean;              // Compatibility prop; currently unused. Use iconButton.
  disabled?: boolean;
  iconButton?: boolean;
};
